import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import axios from 'axios';
import { message } from 'ant-design-vue';

import i18n from '@/i18n';

import { displayLicenseAlert } from '@/state/licenseAlert';

import type { ApiResponse } from '@/api/ApiTypes';

const t = i18n.global.t;
const responseHandlerErrors = new WeakSet<object>();


const isObject = (value: unknown): value is object => typeof value === 'object' && value != null;


export const isResponseHandlerError = (error: unknown): boolean => (
	isObject(error) && responseHandlerErrors.has(error)
);


const applyTransform = (
	obj: object,
	func: (key: string) => string,
): Record<string, unknown> => {
	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(obj)) {
		const field: unknown = value;
		result[func(key)] = field;
	}
	return result;
};


export const toSnakeCase = (
	obj: object,
): Record<string, unknown> => applyTransform(obj, snakeCase);


export const toCamelCase = (
	obj: object,
): Record<string, unknown> => applyTransform(obj, camelCase);


export const toCamelCaseList = (
	lst: readonly object[],
): Record<string, unknown>[] => lst.map(toCamelCase);


export const isRequestCanceled = (error: unknown): boolean => {
	if (axios.isCancel(error)) {
		return true;
	}
	if (typeof error !== 'object' || error == null) {
		return false;
	}
	const candidate = error as { code?: unknown; name?: unknown; message?: unknown };
	return (
		candidate.code === 'ERR_CANCELED'
		|| candidate.name === 'CanceledError'
		|| candidate.message === 'canceled'
	);
};


// Keep Axios error shape checks in one place: callers only validate the expected field type.
const getResponseField = <T>(
	error: unknown,
	field: 'detail' | 'status',
): T | null => {
	if (typeof error !== 'object' || error == null || !('response' in error)) {
		return null;
	}
	const data = (error as { response?: { data?: Record<string, unknown>; status?: unknown } }).response;
	const value = field === 'status' ? data?.status : data?.data?.detail;
	return (value ?? null) as T | null;
};


export const logError = (error: unknown): void => {
	if (isResponseHandlerError(error)) {
		return;
	}
	const requestConfig = (
		typeof error === 'object' && error != null && 'config' in error
			? (error as { config?: { method?: unknown; url?: unknown } }).config
			: undefined
	);
	console.warn('api request failed', {
		canceled: isRequestCanceled(error),
		code: typeof error === 'object' && error != null && 'code' in error
			? (error as { code?: unknown }).code
			: undefined,
		name: typeof error === 'object' && error != null && 'name' in error
			? (error as { name?: unknown }).name
			: undefined,
		message: typeof error === 'object' && error != null && 'message' in error
			? (error as { message?: unknown }).message
			: undefined,
		stack: error instanceof Error ? error.stack : undefined,
		method: requestConfig?.method,
		url: requestConfig?.url,
		visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
	});
	const detail = getResponseField<string>(error, 'detail');
	const errorMessage = typeof detail === 'string' ? detail : null;
	const outputMessage = (errorMessage == null) ? t('common.unknownError') : errorMessage;
	message.error(outputMessage + t('common.reloginError'));
};


export const getAxiosErrorStatus = (error: unknown): number | null => {
	const status = getResponseField<number>(error, 'status');
	return typeof status === 'number' ? status : null;
};


// label kept optional to match Api.js's extractData(handler, label) debug signature.
export const extractData = <T>(
	handler: (data: T) => void,
	label?: string,
): ((response: ApiResponse<T>) => void) => response => {
		if (label) {
			console.log(label, response);
		}
		// During page teardown Firefox can resolve an XHR with HTTP status 0 and
		// an empty object. It is not an API response and must not reach endpoint handlers.
		if (response.status === 0) {
			console.debug('api response interrupted', {
				method: response.config?.method,
				url: response.config?.url,
				visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
			});
			return;
		}
		if (response.data.status === 'license_error') {
			displayLicenseAlert();
			return;
		}
		try {
			handler(response.data);
		} catch (error) {
			if (isObject(error)) {
				responseHandlerErrors.add(error);
			}
			console.error('api response handler failed', {
				name: error instanceof Error ? error.name : undefined,
				message: error instanceof Error ? error.message : String(error),
				stack: error instanceof Error ? error.stack : undefined,
				method: response.config?.method,
				url: response.config?.url,
				httpStatus: response.status,
				apiStatus: response.data.status,
				responseKeys: Object.keys(response.data),
				visibility: typeof document === 'undefined' ? undefined : document.visibilityState,
			}, error);
			throw error;
		}
	};
